import type { PreviewContract } from "../contract";
import type { EmbedConfig, ResolvedPreviewTarget } from "./types";

export interface PreviewFetcher {
  fetchPreview(
    target: ResolvedPreviewTarget,
    signal?: AbortSignal,
  ): Promise<PreviewContract | undefined>;
  clearCache(): void;
}

export function createPreviewFetcher(
  config: Pick<EmbedConfig, "apiBase">,
): PreviewFetcher {
  const apiBase = config.apiBase.replace(/\/+$/, "");
  const cache = new Map<string, PreviewContract>();

  return {
    async fetchPreview(target, signal) {
      const cached = cache.get(target.cacheKey);
      if (cached) {
        return cached;
      }

      const response = await fetch(
        `${apiBase}/api/preview/v1/${target.routeSegment}/${encodeURIComponent(target.normalizedIdentifier)}`,
        {
          method: "GET",
          headers: {
            Accept: "application/json",
          },
          signal,
        },
      );

      if (response.status === 404) {
        return undefined;
      }

      if (!response.ok) {
        throw new Error(
          `Preview API returned ${response.status} for ${target.cacheKey}`,
        );
      }

      const payload = (await response.json()) as PreviewContract;
      cache.set(target.cacheKey, payload);
      return payload;
    },

    clearCache() {
      cache.clear();
    },
  };
}
